import { ReactNode, useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Textarea } from 'core-components/library/inputs'
import { Tag, TQualityBand } from 'partner360/components/qualityIndicator'
import {
  IFinancialPlanQuality,
  IFinancialPlanQualityCategoryScore,
} from 'partner360/features/financialPlanQuality'
import { TRANSLATION_KEYS as FINANCIAL_TRANSLATION_KEYS } from 'partner360/features/financialPlanQuality/configs/translations'
import { getDescriptionQualityReasonTranslation } from 'partner360/features/keyInitiatives/ui/helpers/getDescriptionQualityReasonTranslation'

import { PLAN_REVIEW_JUSTIFICATION_MIN_LENGTH } from '../config/justification'
import { getPlanReviewItem, PLAN_REVIEW_GROUPS } from '../config/steps'
import { PLAN_REVIEW_TRANSLATIONS } from '../config/translations'
import { isBelowHighQuality } from '../lib/isBelowHighQuality'
import { isPlanReviewJustificationValid } from '../lib/isPlanReviewJustificationValid'
import type { IPlanningMeasureQuality } from '../model/planningMeasure'
import {
  PlanReviewSection,
  TPlanReviewJustifications,
  TPlanReviewQualitySection,
  TPlanReviewRole,
} from '../model/types'
import { TInitiativesPlanQuality } from '../model/usePlanReviewQuality'

import { PlanningMeasureSummary } from './PlanningMeasureStep'
import { PlanReviewText } from './PlanReviewText'

import styles from './PlanReviewSummaryStep.module.scss'

interface IPlanReviewSummaryStepProps {
  role: TPlanReviewRole
  financialQuality: IFinancialPlanQuality
  initiativesQuality: TInitiativesPlanQuality
  planningMeasureQuality?: IPlanningMeasureQuality
  isReviewBlocked?: boolean
  weakSections: readonly TPlanReviewQualitySection[]
  justifications: TPlanReviewJustifications
  isSaving: boolean
  saveError: string | null
  onSectionChange: (section: TPlanReviewQualitySection) => void
  onSaveOverall: (value: string) => Promise<boolean>
  onAcknowledge: () => void
}

interface IQualityBadgeProps {
  score: number | null
  band: TQualityBand | null
}

const QualityBadge = ({ score, band }: IQualityBadgeProps) => (
  <Tag variant={band ?? undefined} translation={score === null ? '-' : `${score}%`} />
)

const formatGrowthValue = (value: number | null, valueUnit: 'percent' | 'percentagePoints') => {
  if (value === null) {
    return '-'
  }

  return `${value > 0 ? '+' : ''}${value.toFixed(1)}${valueUnit === 'percent' ? '%' : ' pp'}`
}

const hasFinancialIssue = (item: IFinancialPlanQualityCategoryScore): boolean =>
  isBelowHighQuality(item.band) || Boolean(item.children?.some(hasFinancialIssue))

const FinancialIssueList = ({
  items,
  valueUnit,
}: {
  items: IFinancialPlanQualityCategoryScore[]
  valueUnit: 'percent' | 'percentagePoints'
}) => {
  const problemItems = items.filter(hasFinancialIssue)

  if (!problemItems.length) {
    return null
  }

  return (
    <ul className={styles.issueList}>
      {problemItems.map((item) => {
        const isItemWeak = isBelowHighQuality(item.band)

        return (
          <li key={item.id} className={styles.issueItem}>
            <div className={styles.issueRow}>
              <span className={styles.issueName}>
                {item.offering && (
                  <>
                    <Translation text={item.offering} span />
                    {' - '}
                  </>
                )}
                <Translation text={item.category} span />
              </span>
              {isItemWeak && (
                <span className={styles.issueMetrics}>
                  <span>
                    <Translation
                      text={FINANCIAL_TRANSLATION_KEYS.quality.details.plannedYoY}
                      span
                    />
                    {': '}
                    {formatGrowthValue(item.planned, valueUnit)}
                  </span>
                  <span>
                    <Translation
                      text={FINANCIAL_TRANSLATION_KEYS.quality.details.marketTarget}
                      span
                    />
                    {': '}
                    {formatGrowthValue(item.target, valueUnit)}
                  </span>
                  <QualityBadge score={item.points} band={item.band} />
                </span>
              )}
            </div>
            {item.children && <FinancialIssueList items={item.children} valueUnit={valueUnit} />}
          </li>
        )
      })}
    </ul>
  )
}

const FinancialSummaryDetails = ({
  pillar,
}: {
  pillar: IFinancialPlanQuality['financialGrowth']
}) => {
  const scoredCategories = pillar.categories.filter(({ band }) => band !== null)
  const issueCount = scoredCategories.filter(({ band }) => isBelowHighQuality(band)).length

  return (
    <>
      <div className={styles.issueSummary}>
        <PlanReviewText
          text={PLAN_REVIEW_TRANSLATIONS.summary.itemsRequireAttention}
          replace={{ issueCount, totalCount: scoredCategories.length }}
        />
      </div>
      <FinancialIssueList items={pillar.categories} valueUnit={pillar.valueUnit} />
    </>
  )
}

const SectionSummary = ({
  section,
  score,
  band,
  onSectionChange,
  children,
}: {
  section: TPlanReviewQualitySection
  score: number | null
  band: TQualityBand | null
  onSectionChange: (section: TPlanReviewQualitySection) => void
  children: ReactNode
}) => {
  const item = getPlanReviewItem(section)

  return (
    <section className={styles.sectionSummary}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          {item && <Translation text={item.title} span />}
          <QualityBadge score={score} band={band} />
        </div>
        <Button size="sm" variant="outlined" onClick={() => onSectionChange(section)}>
          <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.reviewDetails} />
        </Button>
      </div>
      {children}
    </section>
  )
}

const InitiativesWeakSummary = ({
  quality,
  weakSectionSet,
  onSectionChange,
}: {
  quality: TInitiativesPlanQuality
  weakSectionSet: Set<TPlanReviewQualitySection>
  onSectionChange: (section: TPlanReviewQualitySection) => void
}) => (
  <section className={styles.weakPointGroup}>
    <div className={styles.groupHeader}>
      <h3>
        <Translation text={PLAN_REVIEW_GROUPS[1].title} />
      </h3>
      <QualityBadge {...quality.overall} />
    </div>

    {weakSectionSet.has(PlanReviewSection.FinancialPlanCoverage) && (
      <SectionSummary
        section={PlanReviewSection.FinancialPlanCoverage}
        score={quality.financialPlanCoverage.score}
        band={quality.financialPlanCoverage.band}
        onSectionChange={onSectionChange}
      >
        <div className={styles.issueSummary}>
          <PlanReviewText
            text={PLAN_REVIEW_TRANSLATIONS.summary.missingFinancialTargets}
            replace={{
              issueCount: quality.financialPlanCoverage.initiativesWithoutFinancialTargets.length,
            }}
          />
        </div>
        <ul className={styles.issueList}>
          {quality.financialPlanCoverage.initiativesWithoutFinancialTargets.map((initiative) => (
            <li key={initiative.EntryId} className={styles.issueItem}>
              <div className={styles.issueRow}>
                <span className={styles.issueName}>
                  {initiative.Name || `Initiative #${initiative.EntryId}`}
                </span>
                <span className={styles.issueDescription}>
                  <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.financialTargetMissing} />
                </span>
              </div>
            </li>
          ))}
        </ul>
      </SectionSummary>
    )}

    {weakSectionSet.has(PlanReviewSection.DescriptionQuality) && (
      <SectionSummary
        section={PlanReviewSection.DescriptionQuality}
        score={quality.descriptionQuality.score}
        band={quality.descriptionQuality.band}
        onSectionChange={onSectionChange}
      >
        <div className={styles.issueSummary}>
          <PlanReviewText
            text={PLAN_REVIEW_TRANSLATIONS.summary.descriptionsRequireAttention}
            replace={{
              issueCount: quality.descriptionQuality.lowScoredCount,
              totalCount: quality.descriptionQuality.totalCount,
            }}
          />
        </div>
        <ul className={styles.issueList}>
          {quality.descriptionQuality.lowScoredInitiatives.map((initiative) => (
            <li key={initiative.EntryId} className={styles.issueItem}>
              <div className={styles.issueRow}>
                <span className={styles.issueName}>
                  {initiative.Name || `Initiative #${initiative.EntryId}`}
                </span>
                <span className={styles.issueDescription}>
                  {initiative.reasons.map((reason, index) => (
                    <span key={reason}>
                      {index > 0 && ', '}
                      <Translation
                        span
                        text={getDescriptionQualityReasonTranslation(reason, true)}
                      />
                    </span>
                  ))}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </SectionSummary>
    )}

    {weakSectionSet.has(PlanReviewSection.StrategicGrowthAreas) && (
      <SectionSummary
        section={PlanReviewSection.StrategicGrowthAreas}
        score={quality.strategicGrowthAreas.score}
        band={quality.strategicGrowthAreas.band}
        onSectionChange={onSectionChange}
      >
        <div className={styles.issueSummary}>
          <PlanReviewText
            text={PLAN_REVIEW_TRANSLATIONS.summary.prioritiesRequireAttention}
            replace={{
              issueCount:
                quality.strategicGrowthAreas.totalCount - quality.strategicGrowthAreas.coveredCount,
              totalCount: quality.strategicGrowthAreas.totalCount,
            }}
          />
        </div>
        <ul className={styles.issueList}>
          {quality.strategicGrowthAreas.priorityCoverage
            .filter(({ initiatives }) => initiatives.length === 0)
            .map(({ priority }) => (
              <li key={priority.uid} className={styles.issueItem}>
                <div className={styles.issueRow}>
                  <span className={styles.issueName}>{priority.name}</span>
                  <span className={styles.issueDescription}>
                    <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.noLinkedInitiatives} />
                  </span>
                </div>
              </li>
            ))}
        </ul>
      </SectionSummary>
    )}
  </section>
)

const OverallJustificationEditor = ({
  value,
  isSaving,
  saveError,
  firstWeakSection,
  onChange,
  onReview,
  onSave,
}: {
  value: string
  isSaving: boolean
  saveError: string | null
  firstWeakSection?: TPlanReviewQualitySection
  onChange: (value: string) => void
  onReview: (section: TPlanReviewQualitySection) => void
  onSave: () => void
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const normalizedValue = value.trim()
  const isValid = isPlanReviewJustificationValid(value)

  return (
    <section className={styles.justification}>
      <p>
        <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.reviewOrJustify} />
      </p>
      <Textarea
        label={PLAN_REVIEW_TRANSLATIONS.summary.overallJustification.fallback}
        value={value}
        onChange={onChange}
        rows={5}
        resizable="vertical"
        disabled={isSaving}
        __testId="plan-review-summary-justification"
      />
      <div className={styles.justificationActions}>
        {hasSubmitted && saveError ? (
          <span className={styles.saveError} role="alert">
            {saveError}
          </span>
        ) : (
          <span className={styles.requirement} aria-live="polite">
            Minimum {PLAN_REVIEW_JUSTIFICATION_MIN_LENGTH} characters: {normalizedValue.length}/
            {PLAN_REVIEW_JUSTIFICATION_MIN_LENGTH}
          </span>
        )}
        <div className={styles.buttons}>
          <Button
            variant="outlined"
            disabled={!firstWeakSection || isSaving}
            onClick={() => firstWeakSection && onReview(firstWeakSection)}
          >
            <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.reviewFirst} />
          </Button>
          <Button
            disabled={!isValid || isSaving}
            onClick={() => {
              setHasSubmitted(true)
              onSave()
            }}
          >
            <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.saveAndAcknowledge} />
          </Button>
        </div>
      </div>
    </section>
  )
}

const ReadOnlyJustifications = ({
  weakSections,
  justifications,
}: {
  weakSections: readonly TPlanReviewQualitySection[]
  justifications: TPlanReviewJustifications
}) => {
  const entries = [PlanReviewSection.Summary, ...weakSections]
    .map((section) => ({ section, value: justifications[section]?.trim() }))
    .filter((item): item is { section: PlanReviewSection; value: string } => Boolean(item.value))

  return (
    <section className={styles.readOnlyJustifications}>
      <h3>
        <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.pbmJustifications} />
      </h3>
      {entries.length > 0 ? (
        entries.map(({ section, value }) => (
          <div key={section} className={styles.readOnlyJustification}>
            <h4>
              {section === PlanReviewSection.Summary ? (
                <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.overallJustification} />
              ) : section === PlanReviewSection.PlanningMeasure ? (
                <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.planningMeasure.title} />
              ) : (
                <Translation text={getPlanReviewItem(section)?.title ?? section} />
              )}
            </h4>
            <p>{value}</p>
          </div>
        ))
      ) : (
        <p className={styles.emptyJustification}>
          <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.noPbmJustification} />
        </p>
      )}
    </section>
  )
}

export const PlanReviewSummaryStep = ({
  role,
  financialQuality,
  initiativesQuality,
  planningMeasureQuality,
  isReviewBlocked = false,
  weakSections,
  justifications,
  isSaving,
  saveError,
  onSectionChange,
  onSaveOverall,
  onAcknowledge,
}: IPlanReviewSummaryStepProps) => {
  const [overallJustification, setOverallJustification] = useState(
    justifications[PlanReviewSection.Summary] ?? ''
  )
  const weakSectionSet = useMemo(() => new Set(weakSections), [weakSections])
  const financialWeakSections = PLAN_REVIEW_GROUPS[0].items.filter(({ key }) =>
    weakSectionSet.has(key)
  )
  const hasInitiativeWeakSections = PLAN_REVIEW_GROUPS[1].items.some(({ key }) =>
    weakSectionSet.has(key)
  )

  useEffect(() => {
    setOverallJustification(justifications[PlanReviewSection.Summary] ?? '')
  }, [justifications])

  const handleSaveAndAcknowledge = async () => {
    if (
      isSaving ||
      isReviewBlocked ||
      !isPlanReviewJustificationValid(overallJustification) ||
      !(await onSaveOverall(overallJustification.trim()))
    ) {
      return
    }

    onAcknowledge()
  }

  return (
    <div className={styles.summary}>
      <header className={styles.intro}>
        <h2>
          <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.congratulations} />
        </h2>
        <p>
          <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.weakPointsIntro} />
        </p>
      </header>

      {weakSections.length === 0 ? (
        !isReviewBlocked && (
          <div className={styles.successMessage}>
            <i className="kbase kbase-check-circle" aria-hidden />
            <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.noWeakPoints} />
          </div>
        )
      ) : (
        <div className={styles.weakPointGroups}>
          {financialWeakSections.length > 0 && (
            <section className={styles.weakPointGroup}>
              <div className={styles.groupHeader}>
                <h3>
                  <Translation text={PLAN_REVIEW_GROUPS[0].title} />
                </h3>
                <QualityBadge {...financialQuality.overall} />
              </div>

              {financialWeakSections.map(({ key }) => {
                const pillar =
                  key === PlanReviewSection.FinancialGrowth
                    ? financialQuality.financialGrowth
                    : financialQuality.sobGrowth

                return (
                  <SectionSummary
                    key={key}
                    section={key}
                    score={pillar.score}
                    band={pillar.band}
                    onSectionChange={onSectionChange}
                  >
                    <FinancialSummaryDetails pillar={pillar} />
                  </SectionSummary>
                )
              })}
            </section>
          )}

          {planningMeasureQuality && weakSectionSet.has(PlanReviewSection.PlanningMeasure) && (
            <PlanningMeasureSummary
              quality={planningMeasureQuality}
              onReview={() => onSectionChange(PlanReviewSection.PlanningMeasure)}
            />
          )}

          {hasInitiativeWeakSections && (
            <InitiativesWeakSummary
              quality={initiativesQuality}
              weakSectionSet={weakSectionSet}
              onSectionChange={onSectionChange}
            />
          )}
        </div>
      )}

      {weakSections.length > 0 &&
        (role === 'hp' ? (
          <OverallJustificationEditor
            value={overallJustification}
            isSaving={isSaving || isReviewBlocked}
            saveError={saveError}
            firstWeakSection={weakSections[0]}
            onChange={setOverallJustification}
            onReview={onSectionChange}
            onSave={() => void handleSaveAndAcknowledge()}
          />
        ) : (
          <ReadOnlyJustifications weakSections={weakSections} justifications={justifications} />
        ))}
    </div>
  )
}
